import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";

export const useImportPlanDrawerViewModel = ({
  stateManager,
  onClose,
  navigate,
  tab,
  accountId,
  seriesOrCommunityId,
}) => {
  const [search, setSearch] = useState("");
  const [openResultsContainer, setOpenResultsContainer] = useState(false);
  const [specPlans, setSpecPlans] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [specLevelOptions, setSpecLevelOptions] = useState([]);
  const [filteredSeriesOptions, setFilteredSeriesOptions] = useState([]);
  const [filteredSpecLevelOptions, setFilteredSpecLevelOptions] = useState([]);
  const [filter, setFilter] = useState({ series: [], specLevel: [] });

  const debouncedSearch = useDebounce(search, 300);
  const GET_SERIES_OPTIONS_UUID = createUUID();
  const GET_SPEC_PLANS_UUID = createUUID();
  const HANDLE_IMPORT_PLAN_UUID = createUUID();

  const getSeriesOptions = () => {
    stateManager.abort(GET_SERIES_OPTIONS_UUID);
    stateManager.run(async () => {
      const response = await homeService.series.getAll({
        sort: "name",
        filters: { accountId: { type: "guid", value: accountId } },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setSeriesOptions(response.data);
        setSpecLevelOptions(response.data);
        setOpenResultsContainer(true);
      }
    }, GET_SERIES_OPTIONS_UUID);
  };

  const getSpecPlans = () => {
    const { series, specLevel } = filter;

    const seriesIds = specLevelOptions
      .filter((o) => series.includes(o.name))
      .map((o) => o.id);

    const filters = [
      {
        isImported: false,
        isItemTemplate: false,
        // planStatus: { in: ["Active"] }, //TODO: Uncomment when draft plan is done with review screen
        builderId: { type: "guid", value: accountId },
        name: { contains: debouncedSearch },
        ...(series.length > 0 && { seriesId: { in: seriesIds } }),
        ...(specLevel.length > 0 && { specLevel: { in: specLevel } }),
      },
    ];

    stateManager.abort(GET_SPEC_PLANS_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.getAll({
        sort: "name",
        filters,
      });
      if (response.status !== HTTP_STATUSES.OK) {
        return;
      }
      if (series.length === 0) {
        const filteredOptions = seriesOptions.filter((o) =>
          response.data.some((d) => d.series === o.name)
        );
        setFilteredSeriesOptions(filteredOptions);
      }
      if (specLevel.length === 0) {
        const filteredOptions = specLevelOptions.filter((o) =>
          response.data.some((d) => d.specLevel === o.specLevel)
        );
        setFilteredSpecLevelOptions(filteredOptions);
      }
      setSpecPlans(response.data);
    }, GET_SPEC_PLANS_UUID);
  };

  const handleImportPlan = (id) => {
    stateManager.abort(HANDLE_IMPORT_PLAN_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.importPlan({
        id,
        data: {
          ...(tab === "communities"
            ? { communityId: seriesOrCommunityId }
            : { seriesId: seriesOrCommunityId }),
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        const planId = response.data;
        navigate(
          `/estimating/bids/${accountId}/${tab}/${seriesOrCommunityId}/bid-import-review/${planId}`
        );
      }
    }, HANDLE_IMPORT_PLAN_UUID);
  };

  const handleSeriesOptionFilter = (option) => {
    const isSelected = filter.series.includes(option.name);
    setOpenResultsContainer(true);
    if (isSelected) {
      setFilter((prev) => ({
        ...prev,
        series: prev.series.filter((series) => series !== option.name),
        specLevel: [],
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        series: [...prev.series, option.name],
        specLevel: [],
      }));
    }
  };

  const handleSpecLevelOptionFilter = (option) => {
    const isSelected = filter.specLevel.includes(option.specLevel);
    setOpenResultsContainer(true);
    if (isSelected) {
      setFilter((prev) => ({
        ...prev,
        specLevel: prev.specLevel.filter(
          (specLevel) => specLevel !== option.specLevel
        ),
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        specLevel: [...prev.specLevel, option.specLevel],
      }));
    }
  };

  const handleCloseResultContainer = () => {
    setSearch("");
    setOpenResultsContainer(false);
    setFilter({ series: [], specLevel: [] });
  };

  const handleCloseDrawer = () => {
    onClose();
    handleCloseResultContainer();
  };

  const handleSearch = (value) => {
    setSearch(value);
    setOpenResultsContainer(true);
  };

  return {
    isLoading: stateManager.isBusy(),
    search,
    filter,
    openResultsContainer,
    debouncedSearch,
    specPlans,
    seriesOptions,
    filteredSeriesOptions,
    filteredSpecLevelOptions,
    setSearch,
    getSeriesOptions,
    getSpecPlans,
    handleSearch,
    handleSeriesOptionFilter,
    handleSpecLevelOptionFilter,
    handleCloseResultContainer,
    handleCloseDrawer,
    handleImportPlan,
  };
};
